import styled, { css } from 'styled-components';
import { ArrowRightIcon as ArrowRightIcon$1 } from '../Icons/components/ArrowRight.js';
import { Button } from '../Buttons/ButtonV2/index.js';
import { elevation } from '../Styles/Utils/elevation.js';

const defaultSizeInPixels = 48;
const FunctionalButton = styled.button.attrs({ type: 'button' }) `
    /* Reset default browser styles */
    all: unset;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    :disabled {
        cursor: default;
    }
`;
const Link = styled.a.attrs({ rel: 'noopener noreferrer' }) `
    /* Reset default browser styles */
    all: unset;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
`;
const CircleContainer = styled.div `
    width: ${defaultSizeInPixels}px;
    height: ${defaultSizeInPixels}px;
    grid-area: icon;
    place-self: center left;
    position: relative;
`;
const CircleBadge = styled.div `
    position: absolute;
    right: 0;
    bottom: 0;
    overflow: hidden;
    display: flex;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;
    min-width: 20px;
    max-width: 100%;
    height: 20px;
    border-radius: 20px;
    border-style: solid;
    border-width: ${(props) => (props.borderColor ? 2 : 0)}px;
    border-color: ${(props) => { var _a; return (_a = props.borderColor) !== null && _a !== void 0 ? _a : 'transparent'; }};
    background-color: ${(props) => props.backgroundColor || props.theme.colors.ElementBackground};

    ${elevation(1, false)}
`;
const CircleBadgeText = styled.span `
    ${(props) => css(props.theme.typography.SuperTinyHeadline)}
    color: ${(props) => { var _a; return (_a = props.color) !== null && _a !== void 0 ? _a : props.theme.colors.Foreground; }};
    font-size: 12px;
    line-height: 20px;
    padding: 0 2px;

    overflow: hidden;
    white-space: nowrap;
    text-align: left;
    text-overflow: ellipsis;
`;
const Title = styled.p `
    ${(props) => css(props.theme.typography.SuperTinyHeadline)}
    color: ${(props) => { var _a; return (_a = props.color) !== null && _a !== void 0 ? _a : props.theme.colors.Foreground; }};

    grid-area: title;
    align-self: end;
    max-width: 100%;

    overflow: hidden;
    white-space: nowrap;
    text-align: left;
    text-overflow: ellipsis;
    margin: auto 0;
`;
const Description = styled.p `
    ${(props) => css(props.theme.typography.TinyParagraph)}
    color: ${(props) => { var _a; return (_a = props.color) !== null && _a !== void 0 ? _a : props.theme.colors['Level 5']; }};

    margin: 0;
    grid-area: description;
    align-self: start;
    max-width: 100%;

    overflow: hidden;
    white-space: nowrap;
    text-align: left;
    text-overflow: ellipsis;
`;
const ArrowRightIcon = styled(ArrowRightIcon$1) `
    grid-area: action-pointer;
    place-self: center end;
`;
const AdditionalButton = styled(Button) `
    grid-area: action-pointer;
    place-self: center end;

    z-index: 1;

    &:hover {
        ${elevation(2, true)}
    }

    > * {
        font-weight: 500;
    }
`;
const RightContent = styled.div `
    grid-area: action-pointer;
    place-self: center end;
    font-size: ${(props) => props.theme.typography.TinyParagraph.fontSize};
    color: ${(props) => { var _a; return (_a = props.color) !== null && _a !== void 0 ? _a : props.theme.colors['Level 5']; }};

    z-index: 1;

    > * {
        font-weight: ${(props) => props.theme.typography.TinyParagraph.fontWeight};
    }
`;
const Circle = styled.div `
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    ${({ variant, borderColor, backgroundColor, theme: { colors: { stroke: { lighterGrey }, }, spacers: { XS }, }, }) => variant == 'circle'
    ? `
                border-radius: 50%;
                border-width: ${XS};
                border-style: solid;
                border-color: ${borderColor !== null && borderColor !== void 0 ? borderColor : 'transparent'};
                background-color: ${backgroundColor !== null && backgroundColor !== void 0 ? backgroundColor : lighterGrey};
                `
    : `
                border-radius: 4px;
            `}
    overflow: hidden;
    box-sizing: border-box;

    > *:nth-child(2) {
        z-index: 1;
    }
`;
const Delimiter = styled.div `
    grid-area: delimiter;
    grid-column: 2 / -1;
    background-color: ${(props) => props.theme.colors['Level 1']};
`;
const RippleContainer = styled.div `
    grid-column: 1 / -1;
    grid-row: 1 / -2;
    border-radius: 4px;
`;
const Content = styled.div `
    position: relative;
    display: grid;
    grid-row-gap: 0;
    grid-column-gap: 8px;
    grid-template-columns: auto 1fr auto;
    grid-template-rows:
        12px
        auto
        5px
        auto
        12px
        ${(props) => (props.hasDelimiter ? 2 : 0)}px;
    grid-template-areas:
        '. . .'
        'icon title action-pointer'
        'icon . action-pointer'
        'icon description action-pointer'
        '. . .'
        'delimiter delimiter delimiter';

    ${(props) => props.isTransparent &&
    css `
            ${Circle}, ${Title}, ${Description}, ${ArrowRightIcon} {
                opacity: 0.5;
            }
        `}

    ${(props) => !props.hasDescription &&
    css `
            grid-template-rows:
                12px
                auto
                12px
                ${props.hasDelimiter ? 2 : 0}px;
            grid-template-areas:
                '. . .'
                'icon title action-pointer'
                '. . .'
                'delimiter delimiter delimiter';
        `}
`;

export { AdditionalButton, ArrowRightIcon, Circle, CircleBadge, CircleBadgeText, CircleContainer, Content, Delimiter, Description, FunctionalButton, Link, RightContent, RippleContainer, Title };
